/**
 * dview:AlongTrackViewer: the track as lanes on one km axis. Objects as symbols, speed as
 * steps per profile, gradient as a height profile, curves as bumps by radius, contact line sections as bands. Stretches a
 * lane has no data for are hatched. Moving the pointer (or the arrow keys) scrubs a km and the
 * readout gives every lane at that km. The label column carries a line per lens.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type SVGProps,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ToggleButton, type IconComponent } from '@bn-apps/dim-ui-react';
import { TJN_DRAWINGS, TjnSignal, findTjn } from '@bn-apps/dim-ui-react/tjn';
import { Legend, LegendToggle } from './legend';
import { useStored } from '../stored';
import tjnStyles from './tjn.module.scss';
import { resultLevel } from '@hooks/useValidationResults';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { pickLang, setting } from '@utils/viewShapes.utils';
import { prettyPath } from '../page';
import {
  formatKm,
  localName,
  nearestMarker,
  segmentsAt,
  type Lane,
  type LinearLocation,
} from '../linearLocation';
import { uriText } from './values';
import type { Widget, WidgetContext } from './types';
import { useNumberFormat } from './figures';
import { signValue } from './signValue';
import styles from './alongTrack.module.scss';

const LANE_H = 44;
const OBJECTS_H = 44;
const AXIS_H = 22;
const PAD_R = 8;

function useWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, entry.contentRect.width))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** A step segment's series: its role from dview:categories (basis, plus, tilting), else its category. */
export const profileName = (segment: { role: string | null; category: string | null }) =>
  segment.role ?? localName(segment.category ?? '');

export const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A category as text: the label of an IRI, else the value. */
const categoryText = (category: string | null, context: WidgetContext) =>
  !category ? '' : category.startsWith('http') ? uriText(category, context) : category;

export function ticksFor(from: number, to: number, count: number): number[] {
  const step =
    [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 50000].find(
      (candidate) => (to - from) / candidate <= count
    ) ?? 100000;
  const ticks: number[] = [];
  for (let km = Math.ceil(from / step) * step; km <= to; km += step) ticks.push(km);
  return ticks;
}

function MainSignal({
  x,
  y,
  pole = 8,
  scale = 0.3,
}: {
  x: number;
  y: number;
  pole?: number;
  scale?: number;
}): JSX.Element {
  // The library's 40x64 signal head, raised on a pole standing on y, so it reads as a main signal.
  const head = 64 * scale;
  return (
    <g>
      <path d={`M${x} ${y}V${y - pole}M${x - 3} ${y}H${x + 3}`} className={styles.mainSignalPole} />
      <g transform={`translate(${x - 20 * scale} ${y - pole - head}) scale(${scale})`}>
        <path d="M12 3H28L36 11V53L28 61H12L4 53V11Z" className={styles.mainSignalBody} />
        <circle cx="20" cy="16" r="5.2" className={styles.mainSignalGo} />
        <circle cx="20" cy="32" r="5.2" className={styles.mainSignalOff} />
        <circle cx="20" cy="48" r="5.2" className={styles.mainSignalGo} />
      </g>
    </g>
  );
}

const profileIcons = new Map<string, IconComponent>();
/** The profile's line (colour and dash) as the toggle's icon. */
function profileIcon(profile: string): IconComponent {
  if (!profileIcons.has(profile)) {
    profileIcons.set(profile, (props: SVGProps<SVGSVGElement>) => (
      <svg
        {...props}
        viewBox="0 0 24 24"
        className={`${props.className ?? ''} ${styles.profileIcon}`}
        data-series={profile}
      >
        <path d="M2 12H22" />
      </svg>
    ));
  }
  return profileIcons.get(profile)!;
}

/** A marker's kind in the Show menu: its symbol (dview:symbol, or dview:symbolFor). */
const kindOf = (marker: { symbol: string }): string => marker.symbol;

// The TJN paragraph of each signal head drawn from DIM UI's TJN drawings
const TJN_HEADS: Record<string, string> = {
  'repeater-signal': 'repeater-21',
  'track-signal': '36B',
  'shunting-signal': '42',
  'derailer-signal': '54',
  'level-crossing-signal': '56B',
  'bridge-signal': '56D',
  'atc-sign': '8.64',
};

/**
 * The TJN drawing an object is drawn with, if any: a sign by its own code (the type designator entry), else the
 * lit head of its symbol. A light signal's code names only its paragraph, whose drawing is unlit, so the symbol's
 * head or glyph wins over it.
 */
export function headSignal(symbol: string, code?: string | null): string | null {
  const entry = code ? findTjn(code) : undefined;
  if (entry?.kind === 'sign') return entry.id;
  if (TJN_HEADS[symbol]) return TJN_HEADS[symbol];
  if (symbol === 'main-signal') return null;
  return entry && !entry.id.endsWith('-off') ? entry.id : null;
}

/** A TJN head on a pole standing on y, scaled to the strip like the main signal. */
function TjnHead({
  signal,
  value,
  x,
  y,
  pole = 7,
}: {
  signal: string;
  value?: string;
  x: number;
  y: number;
  pole?: number;
}): JSX.Element | null {
  const entry = findTjn(signal);
  if (!entry) return null;
  const [, , width, height] = TJN_DRAWINGS[entry.id].viewBox;
  const scale = 15 / Math.max(height, width * 0.8);
  return (
    <g>
      <path d={`M${x} ${y}V${y - pole}M${x - 3} ${y}H${x + 3}`} className={styles.mainSignalPole} />
      <TjnSignal
        signal={entry.id}
        value={value}
        label=""
        size={height * scale}
        x={x - (width * scale) / 2}
        y={y - pole - height * scale}
        className={tjnStyles.tjn}
      />
    </g>
  );
}

export function Glyph({
  symbol,
  code,
  value,
  x,
  y,
}: {
  symbol: string;
  code?: string | null;
  value?: string;
  x: number;
  y: number;
}): JSX.Element {
  const signal = headSignal(symbol, code);
  if (signal) return <TjnHead signal={signal} value={value} x={x} y={y} />;
  switch (symbol) {
    case 'main-signal':
      return <MainSignal x={x} y={y} />;
    case 'signal':
      return (
        <rect
          x={x - 4.5}
          y={y - 15}
          width={9}
          height={15}
          rx={4.5}
          className={styles.glyphSignal}
        />
      );
    case 'switch':
      return (
        <rect
          x={x - 4}
          y={y - 8}
          width={8}
          height={8}
          rx={1}
          transform={`rotate(45 ${x} ${y - 4})`}
          className={styles.glyphSwitch}
        />
      );
    case 'balise':
      return (
        <rect x={x - 4} y={y - 6} width={8} height={6} rx={2} className={styles.glyphBalise} />
      );
    case 'platform':
      return (
        <rect x={x - 7} y={y - 5} width={14} height={5} rx={2} className={styles.glyphPlatform} />
      );
    case 'level-crossing':
      return (
        <g>
          <path d={`M${x} ${y}V${y - 7}`} className={styles.signPost} />
          <path
            d={`M${x - 6} ${y - 18}L${x + 6} ${y - 8}M${x + 6} ${y - 18}L${x - 6} ${y - 8}`}
            className={styles.crossBars}
          />
        </g>
      );
    case 'bridge':
      return (
        <g>
          <rect x={x - 7} y={y - 9} width={14} height={8} rx={1} className={styles.bridgeBody} />
          <path
            d={`M${x - 7} ${y - 9}H${x + 7}M${x - 7} ${y - 1}H${x + 7}`}
            className={styles.bridgeRail}
          />
        </g>
      );
    case 'tunnel':
      return (
        <path
          d={`M${x - 7} ${y}V${y - 6}Q${x} ${y - 15} ${x + 7} ${y - 6}V${y}Z`}
          className={styles.tunnelBody}
        />
      );
    // Simplified from the TJN signal library (8.14, 8.23, 8.54, 8.67, 8.68, 8.73)
    case 'distant-signal':
      return (
        <g>
          <path
            d={`M${x} ${y}V${y - 7}M${x - 3} ${y}H${x + 3}`}
            className={styles.mainSignalPole}
          />
          <rect
            x={x - 4}
            y={y - 18}
            width={8}
            height={11}
            rx={2}
            className={styles.mainSignalBody}
          />
          <circle cx={x} cy={y - 12.5} r={2.2} className={styles.lampYellow} />
        </g>
      );
    case 'dwarf-signal':
      return (
        <g>
          <path
            d={`M${x - 5} ${y}V${y - 8}H${x}Q${x + 6} ${y - 8} ${x + 6} ${y - 2}V${y}Z`}
            className={styles.mainSignalBody}
          />
          <circle cx={x - 2.5} cy={y - 5} r={1.4} className={styles.lampWhite} />
          <circle cx={x - 2.5} cy={y - 1.8} r={1.4} className={styles.lampWhite} />
        </g>
      );
    case 'speed-sign':
    case 'gradient-sign':
    case 'km-sign':
      return (
        <g>
          <path d={`M${x} ${y}V${y - 8}`} className={styles.signPost} />
          <rect
            x={x - 5}
            y={y - 16}
            width={10}
            height={symbol === 'km-sign' ? 6 : 8}
            rx={1}
            className={styles.signPlate}
          />
          {symbol === 'gradient-sign' && (
            <path d={`M${x - 3.5} ${y - 9.5}L${x + 3.5} ${y - 14.5}`} className={styles.signMark} />
          )}
          {symbol === 'speed-sign' && (
            <path d={`M${x - 2.5} ${y - 12}H${x + 2.5}`} className={styles.signMark} />
          )}
        </g>
      );
    // A switch by its type, as its hero drawing: a Y, a Y with both legs turning, an X with its slips
    case 'switch-fork':
    case 'switch-curved':
    case 'switch-cross':
    case 'switch-single': {
      const top = y - 16;
      const mid = y - 8;
      const bottom = y;
      const cross = symbol === 'switch-cross' || symbol === 'switch-single';
      return (
        <g>
          {cross ? (
            <>
              <path d={`M${x - 11} ${top}L${x + 11} ${bottom}M${x - 11} ${bottom}L${x + 11} ${top}`} className={styles.switchStraight} />
              <path d={`M${x - 11} ${bottom}Q${x} ${bottom - 7} ${x + 11} ${bottom}`} className={styles.switchBranch} />
              {symbol === 'switch-cross' && <path d={`M${x - 11} ${top}Q${x} ${top + 7} ${x + 11} ${top}`} className={styles.switchBranch} />}
            </>
          ) : (
            <>
              <path d={`M${x - 12} ${mid}H${x - 3}`} className={styles.switchStraight} />
              {symbol === 'switch-fork' ? (
                <>
                  <path d={`M${x - 3} ${mid}H${x + 12}`} className={styles.switchStraight} />
                  <path d={`M${x - 3} ${mid}L${x + 4} ${top}H${x + 12}`} className={styles.switchBranch} />
                </>
              ) : (
                <path d={`M${x - 3} ${mid}Q${x + 4} ${mid} ${x + 12} ${top}M${x - 3} ${mid}Q${x + 4} ${mid} ${x + 12} ${bottom}`} className={styles.switchBranch} />
              )}
            </>
          )}
        </g>
      );
    }
    case 'orientation-post':
      return (
        <g>
          <rect x={x - 1.5} y={y - 14} width={3} height={14} className={styles.signPlate} />
          <path
            d={`M${x - 1.5} ${y - 10}H${x + 1.5}M${x - 1.5} ${y - 5}H${x + 1.5}`}
            className={styles.signMark}
          />
        </g>
      );
    default:
      return <circle cx={x} cy={y - 4} r={4} className={styles.glyphPoint} />;
  }
}

/** An object with a length on the objects lane: a bridge as a deck on an arch, a tunnel as a dark band
 * with portals, anything else (a platform edge) as a bar beside the track. */
function Span({
  symbol,
  x1,
  x2,
  y,
}: {
  symbol: string;
  x1: number;
  x2: number;
  y: number;
}): JSX.Element {
  if (symbol === 'bridge') {
    // As simple as a tunnel: a light band across the track with a railing on each side
    return (
      <g>
        <rect x={x1} y={y - 5} width={x2 - x1} height={10} rx={1} className={styles.bridgeBody} />
        <path d={`M${x1} ${y - 5}H${x2}M${x1} ${y + 5}H${x2}`} className={styles.bridgeRail} />
      </g>
    );
  }
  if (symbol === 'tunnel') {
    return (
      <g>
        <rect x={x1} y={y + 2} width={x2 - x1} height={6} rx={2} className={styles.tunnelBody} />
        <path
          d={`M${x1} ${y + 8}V${y + 4}Q${x1 + 3} ${y + 1} ${x1 + 6} ${y + 4}V${y + 8}M${x2 - 6} ${y + 8}V${y + 4}Q${x2 - 3} ${y + 1} ${x2} ${y + 4}V${y + 8}`}
          className={styles.tunnelPortal}
        />
      </g>
    );
  }
  return (
    <rect x={x1} y={y + 3} width={x2 - x1} height={5} rx={2} className={styles.glyphPlatform} />
  );
}

/** The lane label's second line in the current lens. */
function laneSub(lane: Lane, context: WidgetContext, warnings: number): string {
  switch (context.lens) {
    case 'shape':
      return prettyPath(lane.row, context.prefixes);
    case 'rinf': {
      const rinf = setting(lane.row, 'rinf');
      return rinf ? `era:${localName(rinf)}` : '–';
    }
    case 'quality':
      return [
        warnings > 0 ? `${warnings} !` : '',
        lane.gaps.length > 0 ? `${lane.gaps.length} ?` : '',
      ]
        .filter(Boolean)
        .join(' · ');
    default:
      return '';
  }
}

interface ViewProps {
  stretch: LinearLocation;
  context: WidgetContext;
  begin: string | null;
  end: string | null;
}

const AlongTrackView = ({
  stretch,
  context,
  begin,
  end,
}: ViewProps): JSX.Element => {
  const { t } = useTranslation();
  const [legend, setLegend] = useStored('dim-entity-legend', false);
  const navigate = useNavigate();
  const format = useNumberFormat();
  const [ref, width] = useWidth();
  const [km, setKm] = useState<number | null>(null);
  const speedLane = stretch.lanes.find((lane) => lane.kind === 'step');
  // A topic picked in the side menu shows only its own lanes; its objects stay lit only for the contents topic
  const topic = context.topic;
  const lanes = topic && !topic.overview ? stretch.lanes.filter((lane) => topic.lanes.includes(lane.key)) : stretch.lanes;
  // The objects lane: all objects without a topic or for the contents topic, the topic's own kinds, or none
  // What the topic shows is all the drawing knows: the readout, the legend and the controls read only these
  const markers = !topic || topic.objects ? stretch.markers : stretch.markers.filter((marker) => topic.kinds.includes(kindOf(marker)));
  // The objects lane only when the topic shows objects and some lie on this stretch; none is not "no data"
  const objectsH = markers.length > 0 ? OBJECTS_H : 0;
  const allProfiles = useMemo(
    () =>
      Array.from(new Set((speedLane?.segments ?? []).map((segment) => profileName(segment)))).sort(
        (a, b) => (a === 'basis' ? -1 : b === 'basis' ? 1 : a.localeCompare(b))
      ),
    [speedLane]
  );
  const [profileList, setProfileList] = useStored<string[]>('dim-entity-speed-profiles', ['basis']);
  const profiles = new Set(profileList);
  const setProfiles = (next: Set<string>) => setProfileList([...next]);
  /** The km range zoomed to, by dragging over the lanes or Ctrl + wheel (a pinch on a touchpad). */
  const [view, setView] = useState<[number, number] | null>(null);
  const [drag, setDrag] = useState<[number, number] | null>(null);
  const lens = context.lens ?? 'default';
  const labelW = width < 600 ? 84 : lens === 'shape' || lens === 'rinf' ? 176 : 120;
  const plotW = width - labelW - PAD_R;
  const from = view?.[0] ?? stretch.from;
  const to = view?.[1] ?? stretch.to;
  const span = to - from;
  const x = (metres: number) => ((metres - from) / span) * plotW;
  const kmAt = (clientX: number, box: DOMRect) =>
    Math.min(to, Math.max(from, from + ((clientX - box.left) / box.width) * span));
  const zoomTo = (a: number, b: number) => {
    const lo = Math.max(stretch.from, Math.min(a, b));
    const hi = Math.min(stretch.to, Math.max(a, b));
    // Not closer than 20 m, and the whole track is no zoom
    if (hi - lo < 20) return;
    setView(lo <= stretch.from && hi >= stretch.to ? null : [lo, hi]);
  };

  const resultsByItem = useMemo(() => {
    const map = new Map<string, ReturnType<typeof resultLevel>>();
    for (const result of context.results ?? []) {
      const level = resultLevel(result);
      if (map.get(result.focusNode) !== 'error') map.set(result.focusNode, level);
    }
    return map;
  }, [context.results]);

  const open = (iri: string) => navigate(uriToDereferencePath(iri));
  const title = (iri: string, label: string, detail = '') =>
    `${uriText(iri, context)} · ${label}${detail ? `: ${detail}` : ''}`;
  // A lane is named by the topic that shows it alone (contact line sections: Electrification), else by its own row
  const laneName = (lane: Lane) => {
    const owners = [...(context.model?.shapes?.groups.values() ?? [])].filter((group) =>
      (group.settings.lanes ?? []).flatMap((value) => value.split(/\s+/)).includes(lane.key)
    );
    const only = owners.find((group) => (group.settings.lanes ?? []).flatMap((value) => value.split(/\s+/)).length === 1);
    return capitalise((only && pickLang(only.label, context.language)) ?? pickLang(lane.row.name, context.language) ?? lane.key);
  };

  const onMove = (event: PointerEvent<SVGSVGElement>) => {
    const at = kmAt(event.clientX, event.currentTarget.getBoundingClientRect());
    setKm(at);
    if (drag) setDrag([drag[0], at]);
  };
  const onDown = (event: PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const at = kmAt(event.clientX, event.currentTarget.getBoundingClientRect());
    setDrag([at, at]);
  };
  const onUp = () => {
    // A short drag is a click (opens the object under it)
    if (drag && Math.abs(x(drag[1]) - x(drag[0])) > 8) zoomTo(drag[0], drag[1]);
    setDrag(null);
  };
  const svgRef = useRef<SVGSVGElement>(null);
  const clipId = `along-clip-${useId().replace(/:/g, '')}`;
  const onWheel = useRef<(event: globalThis.WheelEvent) => void>(() => undefined);
  onWheel.current = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const at = kmAt(event.clientX, svgRef.current!.getBoundingClientRect());
    const factor = event.deltaY > 0 ? 1.4 : 1 / 1.4;
    zoomTo(at - (at - from) * factor, at + (to - at) * factor);
  };
  useEffect(() => {
    // React's wheel listener is passive and cannot stop the page zooming
    const svg = svgRef.current;
    const listener = (event: globalThis.WheelEvent) => onWheel.current(event);
    svg?.addEventListener('wheel', listener, { passive: false });
    return () => svg?.removeEventListener('wheel', listener);
  }, []);
  const onKey = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = span / (event.shiftKey ? 10 : 100);
    const current = km ?? from;
    setKm(Math.min(to, Math.max(from, current + (event.key === 'ArrowRight' ? step : -step))));
  };

  const pins: JSX.Element[] = [];
  const pin = (iri: string, px: number, top: number) => {
    const level = lens === 'quality' ? resultsByItem.get(iri) : undefined;
    if (level)
      pins.push(
        <circle
          key={`pin-${iri}-${px}`}
          cx={px}
          cy={top + 6}
          r={5}
          className={styles.pin}
          data-level={level}
        />
      );
  };

  // "No data" where a drawn lane has nothing, on the overview and every topic
  const gapsOf = (lane: Lane, top: number) =>
    lane.gaps.map((gap, index) => (
      <g key={`gap-${index}`}>
        <rect
          x={x(gap.from)}
          y={top + 8}
          width={Math.max(2, x(gap.to) - x(gap.from))}
          height={LANE_H - 14}
          rx={2}
          className={styles.gap}
        >
          <title>{t('entity.template.along.noData')}</title>
        </rect>
        {x(gap.to) - x(gap.from) > 44 && (
          <text x={x(gap.from) + 4} y={top + 6} className={styles.gapText}>
            {t('entity.template.along.noData')}
          </text>
        )}
      </g>
    ));

  const renderLane = (lane: Lane, top: number) => {
    const h = LANE_H;
    const mid = top + h / 2;
    const label = laneName(lane);
    const warnings = lane.segments.filter((segment) => resultsByItem.has(segment.item)).length;
    let body: JSX.Element[] = [];
    if (lane.kind === 'step') {
      const speeds = lane.segments.map((segment) => segment.value ?? 0);
      const low = Math.max(0, Math.floor((Math.min(...speeds) - 20) / 10) * 10);
      const high = Math.max(...speeds, low + 10) + 10;
      const sy = (value: number) => top + h - 8 - ((value - low) / (high - low)) * (h - 16);
      body = allProfiles
        .filter((profile) => profiles.has(profile))
        .flatMap((profile) =>
          lane.segments
            .filter((segment) => profileName(segment) === profile && segment.value !== null)
            .map((segment, index) => {
              pin(segment.item, x((segment.from + segment.to) / 2), top);
              const d = `M${x(segment.from)} ${sy(segment.value!)}H${x(segment.to)}`;
              return (
                <g
                  key={`${profile}-${index}`}
                  className={styles.step}
                  data-series={profile}
                  onClick={() => open(segment.item)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(event) => event.key === 'Enter' && open(segment.item)}
                  aria-label={title(segment.item, label)}
                >
                  <title>
                    {title(segment.item, label, `${format(segment.value!, 0)} ${lane.unit ?? ''}`)}
                  </title>
                  <path d={d} />
                  <path d={d} className={styles.hit} />
                </g>
              );
            })
        );
    } else if (lane.kind === 'bars') {
      // A fixed scale (25 ‰ fills the half lane), so a gentle gradient reads as gentle and up as up
      const max = Math.max(25, ...lane.segments.map((segment) => Math.abs(segment.value ?? 0)));
      body = [
        <path key="zero" d={`M0 ${mid}H${plotW}`} className={styles.zero} />,
        ...lane.segments.map((segment, index) => {
          const value = segment.value ?? 0;
          const barH = Math.max(2, (Math.abs(value) / max) * (h / 2 - 6));
          pin(segment.item, x((segment.from + segment.to) / 2), top);
          return (
            <rect
              key={index}
              x={x(segment.from) + 0.5}
              y={value >= 0 ? mid - barH : mid}
              width={Math.max(1, x(segment.to) - x(segment.from) - 1.5)}
              height={barH}
              rx={1.5}
              className={styles.bar}
              onClick={() => open(segment.item)}
              role="link"
              tabIndex={0}
              onKeyDown={(event) => event.key === 'Enter' && open(segment.item)}
              aria-label={title(segment.item, label)}
            >
              <title>{title(segment.item, label, `${format(value, 1)} ${lane.unit ?? ''}`)}</title>
            </rect>
          );
        }),
      ];
    } else if (lane.kind === 'profile') {
      // The height the gradients add up to (‰ × m / 1000), relative to the start; a gap keeps the height
      const ordered = lane.segments.filter((segment) => segment.value !== null).sort((a, b) => a.from - b.from);
      let height = 0;
      const steps = ordered.map((segment) => {
        const start = height;
        height += (segment.value! * (segment.to - segment.from)) / 1000;
        return { segment, start, end: height };
      });
      const heights = steps.flatMap((step) => [step.start, step.end]);
      const low = Math.min(0, ...heights);
      // At least 2 m of range, so a nearly flat track draws nearly flat
      const range = Math.max(2, Math.max(0, ...heights) - low);
      const hy = (value: number) => top + h - 8 - ((value - low) / range) * (h - 16);
      body = steps.map(({ segment, start, end }, index) => {
        pin(segment.item, x((segment.from + segment.to) / 2), top);
        const value = segment.value!;
        const d = `M${x(segment.from)} ${hy(start)}L${x(segment.to)} ${hy(end)}`;
        return (
          <g
            key={index}
            className={styles.profile}
            onClick={() => open(segment.item)}
            role="link"
            tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && open(segment.item)}
            aria-label={title(segment.item, label)}
          >
            <title>{title(segment.item, label, `${value > 0 ? '+' : ''}${format(value, 1)} ${lane.unit ?? ''}`)}</title>
            <path d={d} />
            <path d={d} className={styles.hit} />
          </g>
        );
      });
    } else if (lane.kind === 'curves') {
      // No base line: straight track, transition curves and missing data must look different
      body = [
        ...lane.segments.map((segment, index) => {
          const r = segment.value ?? 0;
          const x1 = x(segment.from);
          const x2 = x(segment.to);
          pin(segment.item, (x1 + x2) / 2, top);
          // Tighter curves rise higher; the cap keeps radii under 300 m inside the lane.
          const bump = r ? Math.min(16, 5000 / Math.abs(r)) * (r > 0 ? -1 : 1) : 0;
          const detail = `${segment.category ?? ''}${r ? ` R ${format(Math.abs(r), 0)} m` : ''}`;
          return (
            <g
              key={index}
              className={styles.curve}
              onClick={() => open(segment.item)}
              role="link"
              tabIndex={0}
              onKeyDown={(event) => event.key === 'Enter' && open(segment.item)}
              aria-label={title(segment.item, label, detail)}
            >
              <title>{title(segment.item, label, detail)}</title>
              {segment.role === 'arc' && bump !== 0 ? (
                <path d={`M${x1} ${mid}Q${(x1 + x2) / 2} ${mid + 2 * bump} ${x2} ${mid}Z`} />
              ) : (
                <>
                  <path
                    d={`M${x1} ${mid}H${x2}`}
                    className={
                      segment.role === 'transition' ? styles.curveTransition : styles.curveStraight
                    }
                  />
                  <path d={`M${x1} ${mid}H${x2}`} className={styles.hit} />
                </>
              )}
            </g>
          );
        }),
      ];
    } else {
      const labelled = new Set<string>();
      body = lane.segments.map((segment, index) => {
        pin(segment.item, x((segment.from + segment.to) / 2), top);
        const text = categoryText(segment.category, context);
        const showLabel = !labelled.has(text) && x(segment.to) - x(segment.from) > 80;
        if (showLabel) labelled.add(text);
        return (
          <g
            key={index}
            className={styles.band}
            onClick={() => open(segment.item)}
            role="link"
            tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && open(segment.item)}
            aria-label={title(segment.item, label, text)}
          >
            <title>{title(segment.item, label, text)}</title>
            <rect
              x={x(segment.from)}
              y={mid - 3.5}
              width={Math.max(2, x(segment.to) - x(segment.from))}
              height={7}
              rx={3.5}
            />
            {showLabel && (
              <text x={x(segment.from) + 6} y={top + 13} className={styles.bandText}>
                {text}
              </text>
            )}
          </g>
        );
      });
    }
    return { body, gaps: gapsOf(lane, top), warnings };
  };

  const height = objectsH + lanes.length * LANE_H;
  const rendered = lanes.map((lane, index) => {
    const top = objectsH + index * LANE_H;
    return { lane, top, ...renderLane(lane, top) };
  });
  const baseline = 30;
  // Main signals last, so they are drawn in front of the other objects
  const ordered = [...markers].sort(
    (a, b) => Number(kindOf(a) === 'main-signal') - Number(kindOf(b) === 'main-signal')
  );
  const objects = ordered.map((marker, index) => {
    const conceptName = marker.concept ? pickLang(marker.concept.label, context.language) : null;
    const label = marker.number
      ? `${marker.number}${conceptName ? ` · ${conceptName}` : ''}`
      : (pickLang(marker.row.name, context.language) ?? marker.key);
    const px = x((marker.from + marker.to) / 2);
    pin(marker.item, px, 0);
    return (
      <g
        key={index}
        className={styles.marker}
        onClick={() => open(marker.item)}
        role="link"
        tabIndex={0}
        onKeyDown={(event) => event.key === 'Enter' && open(marker.item)}
        aria-label={title(marker.item, label)}
      >
        <title>{title(marker.item, label)}</title>
        {marker.to - marker.from > 1 ? (
          <Span
            symbol={marker.symbol}
            x1={x(marker.from)}
            x2={Math.max(x(marker.from) + 4, x(marker.to))}
            y={baseline}
          />
        ) : (
          <Glyph
            symbol={marker.symbol}
            code={marker.number}
            value={signValue(marker.number, { label: context.resolved[marker.item]?.[0], km: marker.from })}
            x={px}
            y={baseline - 2}
          />
        )}
      </g>
    );
  });

  const readout = (() => {
    if (km === null) return null;
    const parts = [`km ${formatKm(km)}`];
    for (const lane of lanes) {
      const segments = segmentsAt(lane, km);
      if (segments.length === 0) {
        parts.push(`${laneName(lane)}: ${t('entity.template.along.noData')}`);
      } else if (lane.kind === 'step') {
        const values = Array.from(
          new Set(
            segments
              .filter((segment) => profiles.has(profileName(segment)))
              .map((segment) => format(segment.value ?? 0, 0))
          )
        );
        parts.push(`${values.join(' / ')} ${lane.unit ?? ''}`.trim());
      } else if (lane.kind === 'curves') {
        const segment = segments[0];
        parts.push(
          segment.role === 'arc' && segment.value
            ? `R ${format(Math.abs(segment.value), 0)} m`
            : t(
                `entity.template.along.curve.${segment.category ?? 'straight'}`,
                segment.category ?? ''
              )
        );
      } else if (lane.kind === 'bands') {
        parts.push(
          Array.from(
            new Set(segments.map((segment) => categoryText(segment.category, context)))
          ).join(', ')
        );
      } else {
        const value = segments[0].value ?? 0;
        parts.push(`${value > 0 ? '+' : ''}${format(value, 1)} ${lane.unit ?? ''}`.trim());
      }
    }
    const marker = nearestMarker(markers, km, span / 40);
    if (marker)
      parts.push(
        `${uriText(marker.item, context)} ${format(Math.abs((marker.from + marker.to) / 2 - km), 0)} m`
      );
    return parts.join(' · ');
  })();

  const ticks = ticksFor(from, to, Math.max(2, Math.floor(plotW / 90)));
  const tickStep = ticks.length > 1 ? ticks[1] - ticks[0] : 1000;
  const tickDecimals = tickStep >= 1000 ? 0 : tickStep >= 100 ? 1 : 2;
  const kindsPresent = [...new Set(markers.map(kindOf))];

  return (
    <div className={styles.along} ref={ref}>
      <div className={styles.toolbar}>
        <div className={styles.toggles}>
          {allProfiles.length > 1 &&
            speedLane &&
            lanes.includes(speedLane) &&
            allProfiles.map((profile) => (
              <ToggleButton
                key={profile}
                size="xs"
                variant="tertiary"
                isSelected={profiles.has(profile)}
                iconStart={profileIcon(profile)}
                onChange={(selected) => {
                  const next = new Set(profiles);
                  if (selected) next.add(profile);
                  else next.delete(profile);
                  setProfiles(next);
                }}
              >
                {t(`entity.template.along.profile.${profile}`, profile)}
              </ToggleButton>
            ))}
          {view && (
            <Button size="xs" variant="tertiary" onClick={() => setView(null)}>
              {t('entity.template.along.resetZoom', { from: formatKm(from), to: formatKm(to) })}
            </Button>
          )}
          <LegendToggle open={legend} onToggle={setLegend} />
        </div>
      </div>

      {/* What the marks mean, when asked for; the topic in the side menu decides what is drawn */}
      {legend && (
        <Legend
          label={t('entity.template.along.legend')}
          entries={[
            ...kindsPresent.map((kind) => ({
              key: kind,
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" className={styles.legendGlyph}>
                  {kind === 'main-signal' ? (
                    <MainSignal x={9} y={18} pole={4} scale={0.2} />
                  ) : (
                    <Glyph symbol={kind} x={9} y={kind === 'signal' ? 16.5 : 12} />
                  )}
                </svg>
              ),
              label: t(`entity.template.along.symbol.${kind}`),
              count: markers.filter((marker) => kindOf(marker) === kind).length,
            })),
            ...lanes.map((lane) => ({
              key: `lane:${lane.key}`,
              icon: <span className={styles.swatch} data-kind={lane.kind} />,
              label: laneName(lane),
            })),
          ]}
        />
      )}

      <div
        className={styles.trackBar}
        style={{ marginInlineStart: labelW, marginInlineEnd: PAD_R }}
      >
        <span className={styles.trackLine} aria-hidden="true" />
        <span className={styles.trackEnds}>
          <span>
            <b>{t('entity.template.along.start')}</b> {begin ? `${uriText(begin, context)} · ` : ''}
            km {formatKm(stretch.from)}
          </span>
          <span>
            km {formatKm(stretch.to)}
            {end ? ` · ${uriText(end, context)}` : ''} <b>{t('entity.template.along.end')}</b>
          </span>
        </span>
      </div>

      <div className={styles.plot} style={{ gridTemplateColumns: `${labelW}px minmax(0, 1fr)` }}>
        <div className={styles.labels}>
          {objectsH > 0 && (
            <div className={styles.laneLabel} style={{ height: OBJECTS_H }}>
              <span>{t('entity.template.along.objects')}</span>
            </div>
          )}
          {rendered.map(({ lane, warnings }) => (
            <div key={lane.key} className={styles.laneLabel} style={{ height: LANE_H }}>
              <span>{laneName(lane)}</span>
              <span
                className={styles.laneSub}
                data-lens={lens}
                title={laneSub(lane, context, warnings) || undefined}
              >
                {laneSub(lane, context, warnings)}
              </span>
            </div>
          ))}
        </div>
        <svg
          className={styles.svg}
          width={plotW}
          height={height + AXIS_H}
          viewBox={`0 0 ${plotW} ${height + AXIS_H}`}
          onPointerMove={onMove}
          onPointerDown={onDown}
          onPointerUp={onUp}
          onPointerLeave={() => {
            setKm(null);
            setDrag(null);
          }}
          ref={svgRef}
          onKeyDown={onKey}
          tabIndex={0}
          role="group"
          aria-label={t('entity.template.along.label', {
            from: formatKm(stretch.from),
            to: formatKm(stretch.to),
          })}
        >
          <defs>
            <pattern
              id="dim-gap-hatch"
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1="0" y1="0" x2="0" y2="6" className={styles.hatch} />
            </pattern>
            <clipPath id={clipId}>
              <rect x={view ? 0 : -12} y={-4} width={plotW + (view ? 0 : 24)} height={height + 8} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            {rendered.map(({ lane, top }) => (
              <path
                key={`divider-${lane.key}`}
                d={`M0 ${top}H${plotW}`}
                className={styles.divider}
              />
            ))}
            {rendered.map(({ lane, gaps }) => (
              <g key={`gaps-${lane.key}`}>{gaps}</g>
            ))}
            {objectsH > 0 && <path d={`M0 ${baseline}H${plotW}`} className={styles.objectsBase} />}
            {objects}
            {rendered.map(({ lane, body }) => (
              <g
                key={`lane-${lane.key}`}
                className={styles.laneBody}
              >
                {body}
              </g>
            ))}
            {pins}
          </g>
          {ticks.map((tick) => (
            <g key={tick}>
              <path d={`M${x(tick)} ${height}v4`} className={styles.tick} />
              <text x={x(tick)} y={height + 17} textAnchor="middle" className={styles.tickText}>
                {format(tick / 1000, tickDecimals)}
              </text>
            </g>
          ))}
          {drag && Math.abs(x(drag[1]) - x(drag[0])) > 8 && (
            <rect
              x={Math.min(x(drag[0]), x(drag[1]))}
              y={0}
              width={Math.abs(x(drag[1]) - x(drag[0]))}
              height={height}
              className={styles.zoomBox}
            />
          )}
          {km !== null && <path d={`M${x(km)} 0V${height}`} className={styles.scrub} />}
        </svg>
      </div>
      <p className={styles.readout} aria-live="polite">
        {readout ?? <span className={styles.hint}>{t('entity.template.along.hint')}</span>}
      </p>
    </div>
  );
};

export const AlongTrackViewer: Widget = ({ property, context }) => {
  if (!context.stretch) return null;
  const first = (key: string) => {
    const row = context.model?.rows[key];
    return row
      ? ((context.model?.values[row.id] ?? []).find((value) => value.type === 'uri')?.value ?? null)
      : null;
  };
  return (
    <AlongTrackView
      stretch={context.stretch}
      context={context}
      begin={first(setting(property, 'begin') ?? 'begin')}
      end={first(setting(property, 'end') ?? 'end')}
    />
  );
};
